/**
 * Hash routes:
 *   #/                          landing + login (logged out) · หน้าแรก (logged in)
 *   #/demo                      anonymous demo editor (D01)
 *   #/auth/verify?token=…       finish email magic-link login
 *   #/s/:token                  public viewer of a published link (no login for audience anyone)
 *   #/t/:token                  template link landing
 *   #/d/:designId               editor
 *   #/present/:designId         presenter view (?ch= audience sync, ?mode=single, ?rev=, ?from=)
 *   #/present/:designId/audience  audience window
 *   #/presentations  #/import/pptx  #/templates  #/campaign  #/csv  #/settings
 *   #/fonts/licenses            licences of the bundled fonts
 *   #/catalog  #/catalog/items/:itemId  #/catalog/import  #/catalog/deck
 *   #/tor  #/tor/new  #/tor/drafts/:draftId  #/tor/library  #/tor/templates  #/tor/templates/:templateId
 *   #/convert                   แปลง PDF เป็น Word (?mode=tor: PDF → ร่าง TOR + Word)
 *   #/pdf                       เครื่องมือ PDF (?file=<assetId>&tool=organize|sign|fill|annotate|decorate|secure|convert|compare&add=<ids>)
 *   #/files  #/reviews  #/tasks  #/customers  #/customers/:customerId
 *   #/letters (?tab=recipients|settings)  #/letters/:letterId          สารบรรณ: ทะเบียนหนังสือออก (docs/api/LETTERS.md)
 *   #/solutions (?view=board)  #/solutions/new  #/solutions/:opportunityId (?tab=)   Solution Studio, when the workspace has it on (docs/api/SOLUTION_STUDIO.md)
 *   #/photo  #/photo/:assetId (?design= ?mode=pro|express ?tool=)  #/photo/local  #/photo/collage   สตูดิโอภาพ (ADR 0031)
 *   #/reviews (?tab=mine|sent|all|sign)  #/approvals/:requestId (?changes=1)  #/approvals/history/:type/:id   (ADR 0027)
 *   #/sign/:requestId           ขอลายเซ็น: the signing screen / the request's status (ADR 0051)
 *   #/chat  #/chat/:conversationId (?m= message, ?popout=1 own window)  #/channels  #/activity   (ADR 0015)
 *   #/help  #/help/:slug (?for=<path> the article of a page, ?q= search)  #/help/whats-new  #/help/guide  #/help/print  #/help/report   ศูนย์ช่วยเหลือ (ADR 0046)
 *   (any route)                 while the session must change its temporary password: the forced-change page
 *
 * Every page is its own chunk (lib/lazy.ts, ADR 0042): the first screen downloads the app shell
 * and the one page it shows. Links warm their page's chunk before the click (`preloadPath`).
 */
import { Suspense, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { api, describeError, loadApiMore } from './api/client.js';
import type { ApprovalTargetType } from './api/types.js';
import { AppShell } from './app/AppShell.js';
import { dropDesignPrefetch, HOVER_PREFETCH_MS, prefetchDesign, ROUTE_PREFETCH_MS } from './app/designPrefetch.js';
import { usePresenceHeartbeat } from './app/presence.js';
import { useSession } from './app/session.js';
import { ChatHost } from './chat/ChatHost.js';
import { HelpHost } from './help/HelpHost.js';
import { FullPageMessage } from './components/FullPageMessage.js';
import { PageSkeleton } from './components/PageSkeleton.js';
import { lazyPage, whenIdle, type LazyPage } from './lib/lazy.js';
import { rememberReturnTo } from './lib/returnTo.js';
import { SavedAtHost } from './components/folders/SavedAt.js';
import { matchRoute, parseHash, useLocation, type Location } from './lib/router.js';

const DashboardPage = lazyPage(() => import('./pages/DashboardPage.js'), 'DashboardPage');
const PresentationsPage = lazyPage(() => import('./pages/PresentationsPage.js'), 'PresentationsPage');
const FilesPage = lazyPage(() => import('./pages/FilesPage.js'), 'FilesPage');
const DocumentLinksPage = lazyPage(() => import('./pages/DocumentLinksPage.js'), 'DocumentLinksPage');
const FolderPage = lazyPage(() => import('./pages/FolderPage.js'), 'FolderPage');
const TasksPage = lazyPage(() => import('./pages/tasks/TasksPage.js'), 'TasksPage');
const TemplatesPage = lazyPage(() => import('./pages/TemplatesPage.js'), 'TemplatesPage');
const EditorPage = lazyPage(() => import('./pages/EditorPage.js'), 'EditorPage');
const DemoPage = lazyPage(() => import('./pages/DemoPage.js'), 'DemoPage');
const PresentPage = lazyPage(() => import('./pages/PresentPage.js'), 'PresentPage');
const AudiencePage = lazyPage(() => import('./pages/AudiencePage.js'), 'AudiencePage');
const PublicViewerPage = lazyPage(() => import('./pages/PublicViewerPage.js'), 'PublicViewerPage');
const TemplateLinkPage = lazyPage(() => import('./pages/TemplateLinkPage.js'), 'TemplateLinkPage');
const LandingPage = lazyPage(() => import('./pages/LandingPage.js'), 'LandingPage');
const AuthVerifyPage = lazyPage(() => import('./pages/LandingPage.js'), 'AuthVerifyPage');
const PasswordLoginPage = lazyPage(() => import('./pages/PasswordLoginPage.js'), 'PasswordLoginPage');
const ForcePasswordChangePage = lazyPage(() => import('./pages/ForcePasswordChangePage.js'), 'ForcePasswordChangePage');
const CampaignPage = lazyPage(() => import('./pages/CampaignPage.js'), 'CampaignPage');
const CsvPage = lazyPage(() => import('./pages/CsvPage.js'), 'CsvPage');
const SettingsPage = lazyPage(() => import('./pages/SettingsPage.js'), 'SettingsPage');
const FontLicensesPage = lazyPage(() => import('./pages/FontLicensesPage.js'), 'FontLicensesPage');
const AdminAuditPage = lazyPage(() => import('./pages/AdminAuditPage.js'), 'AdminAuditPage');
const AdminPerfPage = lazyPage(() => import('./pages/AdminPerfPage.js'), 'AdminPerfPage');
const PptxImportPage = lazyPage(() => import('./pages/PptxImportPage.js'), 'PptxImportPage');
const PdfImportPage = lazyPage(() => import('./pdfdesign/PdfImportPage.js'), 'PdfImportPage');
const OfficeEditorPage = lazyPage(() => import('./pages/OfficeEditorPage.js'), 'OfficeEditorPage');
const WordEditorPage = lazyPage(() => import('./pages/WordEditorPage.js'), 'WordEditorPage');
const PhotoHomePage = lazyPage(() => import('./pages/PhotoStudioPage.js'), 'PhotoHomePage');
const PhotoCollagePage = lazyPage(() => import('./pages/PhotoStudioPage.js'), 'PhotoCollagePage');
const PhotoStudioPage = lazyPage(() => import('./pages/PhotoStudioPage.js'), 'PhotoStudioPage');
const ReviewsPage = lazyPage(() => import('./pages/ReviewsPage.js'), 'ReviewsPage');
const ApprovalReviewPage = lazyPage(() => import('./pages/approvals/ApprovalReviewPage.js'), 'ApprovalReviewPage');
const ApprovalHistoryPage = lazyPage(() => import('./pages/approvals/ApprovalHistoryPage.js'), 'ApprovalHistoryPage');
// ขอลายเซ็น (ADR 0051): the signing screen is a page of its own — a signer on a phone downloads only this.
const SignPage = lazyPage(() => import('./pdfflow/SignPage.js'), 'SignPage');
const CustomersPage = lazyPage(() => import('./pages/CustomersPage.js'), 'CustomersPage');
const CustomerPage = lazyPage(() => import('./pages/CustomersPage.js'), 'CustomerPage');
const LettersPage = lazyPage(() => import('./pages/letters/LettersPage.js'), 'LettersPage');
const LetterPage = lazyPage(() => import('./pages/letters/LetterPage.js'), 'LetterPage');
// Solution Studio: one chunk for its three pages; a workspace without the module never downloads it.
const loadSolutions = () => import('./pages/solutions/index.js');
const SolutionsListPage = lazyPage(loadSolutions, 'SolutionsListPage');
const SolutionNewPage = lazyPage(loadSolutions, 'SolutionNewPage');
const SolutionPage = lazyPage(loadSolutions, 'SolutionPage');
const ChatPage = lazyPage(() => import('./chat/ChatPage.js'), 'ChatPage');
const ChannelsPage = lazyPage(() => import('./chat/ChannelsPage.js'), 'ChannelsPage');
const ActivityPage = lazyPage(() => import('./chat/ActivityPage.js'), 'ActivityPage');
const CatalogListPage = lazyPage(() => import('./pages/catalog/CatalogListPage.js'), 'CatalogListPage');
const CatalogImportPage = lazyPage(() => import('./pages/catalog/CatalogImportPage.js'), 'CatalogImportPage');
const CreateDocumentPage = lazyPage(() => import('./pages/catalog/CreateDocumentPage.js'), 'CreateDocumentPage');
const ProductDeckPage = lazyPage(() => import('./pages/catalog/ProductDeckPage.js'), 'ProductDeckPage');
const ProductPage = lazyPage(() => import('./pages/catalog/ProductPage.js'), 'ProductPage');
const TorListPage = lazyPage(() => import('./pages/tor/TorListPage.js'), 'TorListPage');
const TorWizardPage = lazyPage(() => import('./pages/tor/TorWizardPage.js'), 'TorWizardPage');
const TorLibraryPage = lazyPage(() => import('./pages/tor/TorLibraryPage.js'), 'TorLibraryPage');
const TorTemplatesPage = lazyPage(() => import('./pages/tor/TorTemplatesPage.js'), 'TorTemplatesPage');
const TorTemplatePage = lazyPage(() => import('./pages/tor/TorTemplatesPage.js'), 'TorTemplatePage');
const TorEditorPage = lazyPage(() => import('./pages/tor/TorEditorPage.js'), 'TorEditorPage');
const ConvertPage = lazyPage(() => import('./pages/ConvertPage.js'), 'ConvertPage');
// เครื่องมือ PDF: only people who open it download its editor.
const PdfToolsPage = lazyPage(() => import('./pages/PdfToolsPage.js'), 'PdfToolsPage');
const ScanPage = lazyPage(() => import('./scan/ScanPage.js'), 'ScanPage');
// ศูนย์ช่วยเหลือ: the articles, the search and the changelog travel with this chunk only (ADR 0046).
const HelpPage = lazyPage(() => import('./pages/HelpPage.js'), 'HelpPage');

type AnyPage = LazyPage<never>;
const chunk = (prefix: string, page: unknown): [string, AnyPage] => [prefix, page as AnyPage];

/** Which page chunk a path needs (first match wins): used to warm a chunk from a link. */
const PAGE_CHUNKS: [string, AnyPage][] = [
  chunk('/doc-links', DocumentLinksPage),
  chunk('/d', EditorPage), chunk('/present', PresentPage), chunk('/presentations', PresentationsPage), chunk('/files', FilesPage), chunk('/folders', FolderPage),
  chunk('/tasks', TasksPage), chunk('/templates', TemplatesPage), chunk('/chat', ChatPage), chunk('/channels', ChannelsPage),
  chunk('/activity', ActivityPage), chunk('/catalog/items', ProductPage), chunk('/catalog/import', CatalogImportPage), chunk('/create-document', CreateDocumentPage),
  chunk('/catalog/deck', ProductDeckPage), chunk('/catalog', CatalogListPage), chunk('/tor/new', TorWizardPage),
  chunk('/tor/library', TorLibraryPage), chunk('/tor/templates', TorTemplatesPage), chunk('/tor/drafts', TorEditorPage),
  chunk('/tor', TorListPage), chunk('/photo', PhotoHomePage), chunk('/customers', CustomersPage), chunk('/letters', LettersPage), chunk('/solutions', SolutionsListPage), chunk('/reviews', ReviewsPage),
  chunk('/approvals', ApprovalReviewPage), chunk('/settings', SettingsPage), chunk('/fonts/licenses', FontLicensesPage), chunk('/admin/audit', AdminAuditPage), chunk('/admin/perf', AdminPerfPage),
  chunk('/convert', ConvertPage), chunk('/pdf', PdfToolsPage), chunk('/scan', ScanPage), chunk('/import/pptx', PptxImportPage), chunk('/import/pdf', PdfImportPage),
  chunk('/campaign', CampaignPage), chunk('/csv', CsvPage), chunk('/help', HelpPage),
  chunk('/sign', SignPage),
];

/** Start downloading the page chunk for an in-app path (nothing happens when it is loaded or unknown). */
export function preloadPath(path: string): void {
  if (path === '/' || path === '') {
    DashboardPage.preload();
    return;
  }
  warmApiMore();
  PAGE_CHUNKS.find(([prefix]) => path === prefix || path.startsWith(prefix + '/'))?.[1].preload();
}

/**
 * The page in the address bar, on a browser that has signed in before: its code is loaded now,
 * while the session request is still on its way (index.html already downloads the files), so the
 * page renders the moment the session is known — not after a Suspense round (lib/lazy.ts).
 */
function preloadCurrentPage(): void {
  try {
    if (typeof window === 'undefined' || !localStorage.getItem('xc.workspace.signin')) return;
    preloadPath(parseHash(window.location.hash).path);
  } catch {
    /* private mode: the page loads when it renders */
  }
}

/** The pages of the main menu, in the order people open them: fetched in the background once the first screen is up. */
const WARM_AFTER_LOAD = ['/', '/presentations', '/d', '/files', '/tasks', '/chat', '/templates', '/activity'];

const saveData = () => !!(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;

/**
 * Links warm their page before the click (pointer over, keyboard focus or touch on any `#/…`
 * link), and after the first screen has settled the main pages are fetched one at a time, so
 * moving around the app does not wait for a download. Data-saver connections skip the background part.
 */
function useChunkPrefetch(signedIn: boolean): void {
  useEffect(() => {
    let hover: ReturnType<typeof setTimeout> | undefined;
    const warm = (e: Event) => {
      const a = e.target instanceof Element ? e.target.closest('a[href^="#/"]') : null;
      if (!a) return;
      const path = parseHash(a.getAttribute('href')!).path;
      preloadPath(path);
      // A link to a design the pointer rests on: its data too, so the editor opens on arrival.
      const design = matchRoute('/d/:designId', path);
      clearTimeout(hover);
      if (design && signedIn && !saveData()) hover = setTimeout(() => prefetchDesign(design.designId!, HOVER_PREFETCH_MS), e.type === 'pointerover' ? 120 : 0);
    };
    const leave = () => clearTimeout(hover);
    const opts = { passive: true } as const;
    document.addEventListener('pointerover', warm, opts);
    document.addEventListener('focusin', warm, opts);
    document.addEventListener('touchstart', warm, opts);
    document.addEventListener('pointerout', leave, opts);
    return () => {
      clearTimeout(hover);
      document.removeEventListener('pointerout', leave);
      document.removeEventListener('pointerover', warm);
      document.removeEventListener('focusin', warm);
      document.removeEventListener('touchstart', warm);
    };
  }, [signedIn]);
  useEffect(() => {
    if (!signedIn) return;
    if (saveData()) return;
    const queue = [...WARM_AFTER_LOAD];
    let timer: ReturnType<typeof setTimeout>;
    // The API methods outside the first screen of หน้าแรก (api/more.ts) — the first thing warmed.
    const idle = whenIdle(warmApiMore, 3000);
    const next = () => {
      const path = queue.shift();
      if (path === undefined) return;
      preloadPath(path);
      timer = setTimeout(next, 300);
    };
    timer = setTimeout(next, 3000);
    return () => {
      idle();
      clearTimeout(timer);
    };
  }, [signedIn]);
}

/** Start fetching api/more.ts (client.ts `loadApiMore`; nothing happens once it is loaded or loading). */
function warmApiMore(): void {
  void loadApiMore().catch(() => undefined);
}

function LoginGate({ notice, returnTo, ssoError }: { notice?: string; returnTo?: string; ssoError?: string | null }) {
  const [mode, setMode] = useState<'loading' | 'magic' | 'password' | 'error'>('loading');
  const [thirteenStore, setThirteenStore] = useState(false);
  const [error, setError] = useState('');
  // Magic links finish in AuthVerifyPage (often a new tab), which returns to consumeReturnTo():
  // remember the page that asked for sign-in so both login modes come back to it (ERR-03).
  useEffect(() => {
    if (returnTo && returnTo !== '/') rememberReturnTo(returnTo);
  }, [returnTo]);
  useEffect(() => {
    let active = true;
    api.providers().then((providers) => {
      if (!active) return;
      setThirteenStore(!!providers.thirteenStore);
      setMode(providers.password ? 'password' : 'magic');
      // The sign-in page's own chunk (and the sign-in requests of api/more.ts), while the person reads the page.
      (providers.password ? PasswordLoginPage : LandingPage).preload();
      warmApiMore();
    }).catch((cause) => {
      if (active) { setError(describeError(cause)); setMode('error'); }
    });
    return () => { active = false; };
  }, []);
  if (mode === 'loading') return <FullPageMessage title="กำลังโหลดหน้าเข้าสู่ระบบ…" busy />;
  if (mode === 'error') return <FullPageMessage title="เชื่อมต่อระบบเข้าสู่ระบบไม่ได้" message={error}><button className="xc-btn xc-btn-primary" onClick={() => window.location.reload()}>ลองอีกครั้ง</button></FullPageMessage>;
  // A refused "เข้าสู่ระบบด้วย 13STORE" comes back as #/login?sso=<code> (routes/sso13store.ts); the
  // login pages put the Thai message to it (components/ssoErrors.ts travels with their chunks, not the shell).
  return mode === 'password'
    ? <PasswordLoginPage notice={notice} returnTo={returnTo} thirteenStore={thirteenStore} ssoError={ssoError} />
    : <LandingPage notice={notice} returnTo={returnTo} thirteenStore={thirteenStore} ssoError={ssoError} />;
}

export function App() {
  return (
    <>
      <Routes />
      {/* Live chat stream, docked chat, calls, Ctrl+K — once per signed-in tab. */}
      <ChatHost />
      {/* "บันทึกไว้ที่: <โฟลเดอร์> · ย้าย…" after anything is created or uploaded (ADR 0043). */}
      <SavedAtHost />
      {/* First-visit coach-marks and the checklist steps only the browser can see (ADR 0046). */}
      <HelpHost />
    </>
  );
}

/** Pages that own the whole window (no app shell): their chunk loads behind a plain full-page status. */
const isFullScreen = (path: string) =>
  path === '/demo' || path === '/auth/verify' || path === '/login' || /^\/(d|s|t|present)\//.test(path) || (/^\/photo\/[^/]+$/.test(path) && path !== '/photo/collage');

/** What shows while a page's chunk is on its way: the app shell with a skeleton, so the menu never disappears. */
function RouteFallback({ path, authed }: { path: string; authed: boolean }) {
  if (!authed || isFullScreen(path)) return <FullPageMessage title="กำลังโหลด…" busy />;
  return (
    <AppShell current={path}>
      <PageSkeleton />
    </AppShell>
  );
}

/** One Suspense boundary per page COMPONENT (not per address): see `Routes`. */
const boundaryIds = new WeakMap<object, number>();
let lastBoundaryId = 0;
function boundaryKey(el: ReactElement): string {
  const type: unknown = el.type;
  if (typeof type !== 'object' && typeof type !== 'function') return String(type);
  let id = boundaryIds.get(type as object);
  if (!id) boundaryIds.set(type as object, (id = ++lastBoundaryId));
  return `page-${id}`;
}

function Routes() {
  useState(preloadCurrentPage);
  const loc = useLocation();
  const session = useSession();
  const authed = session.status === 'authenticated';
  // ทีมออนไลน์: one heartbeat per tab while signed in with a workspace (app/presence.ts).
  usePresenceHeartbeat();
  useChunkPrefetch(authed && !session.mustChangePassword);
  // The address names a design: ask for it and its scene now, while the editor's chunk is on its
  // way (app/designPrefetch.ts). Done while rendering — before the editor page can mount and ask
  // itself — and once per visit of the address.
  const designRoute = authed && !session.mustChangePassword ? matchRoute('/d/:designId', loc.path)?.designId : undefined;
  const prefetched = useRef<string | undefined>(undefined);
  if (prefetched.current !== designRoute) {
    if (prefetched.current) dropDesignPrefetch(prefetched.current);
    prefetched.current = designRoute;
    if (designRoute) prefetchDesign(designRoute, ROUTE_PREFETCH_MS);
  }
  // Every page but หน้าแรก calls methods of api/more.ts: fetch that chunk alongside the page's own.
  if (loc.path !== '/' && loc.path !== '') warmApiMore();
  const element = routeElement(loc, session);
  // A change of page switches at once, as it always did: the boundary is keyed by the page
  // component, so the page being left is UNMOUNTED when another page takes its place — React would
  // otherwise keep it in the document (hidden) while the next page's chunk loads, with its inputs
  // and timers alive. The same page with another address (#/chat/a → #/chat/b) keeps its boundary
  // and its state. The wait itself is rare — links warm their chunk on hover and the main pages are
  // fetched in the background — and shows the app shell with a skeleton, never a blank page.
  return (
    <Suspense key={boundaryKey(element)} fallback={<RouteFallback path={loc.path} authed={authed} />}>
      {element}
    </Suspense>
  );
}

function routeElement(loc: Location, session: ReturnType<typeof useSession>): ReactElement {
  const path = loc.path;

  // Public routes.
  if (path === '/demo') return <DemoPage />;
  if (path === '/auth/verify') return <AuthVerifyPage token={loc.query.get('token')} />;
  const share = matchRoute('/s/:token', path);
  if (share) return <PublicViewerPage key={share.token} token={share.token!} />;

  if (session.status === 'loading') return <FullPageMessage title="กำลังโหลด…" busy />;

  // Signed in with a temporary password: nothing else until it is replaced (the API refuses too).
  // The hash stays as it is, so the intended page opens as soon as the session refreshes.
  if (session.status === 'authenticated' && session.mustChangePassword) return <ForcePasswordChangePage />;

  // Template links show their landing to everyone (login happens there).
  const tpl = matchRoute('/t/:token', path);
  if (tpl) return <TemplateLinkPage key={tpl.token} token={tpl.token!} />;

  const authed = session.status === 'authenticated';
  const requireAuth = (el: ReactElement): ReactElement => (authed ? el : <LoginGate notice="กรุณาเข้าสู่ระบบเพื่อเปิดหน้านี้" returnTo={path + (loc.query.size ? `?${loc.query}` : '')} />);

  if (path === '/' || path === '/login') return authed ? <DashboardPage /> : <LoginGate ssoError={loc.query.get('sso')} />;
  const design = matchRoute('/d/:designId', path);
  if (design) return requireAuth(<EditorPage key={design.designId} designId={design.designId!} />);
  const audience = matchRoute('/present/:designId/audience', path);
  if (audience) return requireAuth(<AudiencePage key={audience.designId} designId={audience.designId!} />);
  const present = matchRoute('/present/:designId', path);
  if (present) return requireAuth(<PresentPage key={present.designId + loc.query.toString()} designId={present.designId!} />);
  if (path === '/presentations') return requireAuth(<PresentationsPage />);
  if (path === '/import/pptx') return requireAuth(<PptxImportPage />);
  if (path === '/import/pdf') return requireAuth(<PdfImportPage />);
  if (path === '/templates') return requireAuth(<TemplatesPage />);
  if (path === '/campaign') return requireAuth(<CampaignPage />);
  if (path === '/csv') return requireAuth(<CsvPage />);
  if (path === '/settings') return requireAuth(<SettingsPage />);
  if (path === '/fonts/licenses') return requireAuth(<FontLicensesPage />);
  if (path === '/admin/audit') return requireAuth(<AdminAuditPage />);
  // ความเร็วที่ผู้ใช้เห็นจริง: speed as the employees' devices measure it (docs/api/PERF.md).
  if (path === '/admin/perf') return requireAuth(<AdminPerfPage />);
  // One person's timeline across every category (ADR 0035).
  const auditPerson = matchRoute('/admin/audit/person/:id', path);
  if (auditPerson) return requireAuth(<AdminAuditPage key={auditPerson.id} personId={auditPerson.id!} />);
  if (path === '/files') return requireAuth(<FilesPage />);
  if (path === '/doc-links') return requireAuth(<DocumentLinksPage />);
  // A Word / Excel / PowerPoint file in the in-browser editor (ADR 0047 §5).
  const office = matchRoute('/office/:assetId', path);
  if (office) return requireAuth(<OfficeEditorPage key={office.assetId + loc.query.toString()} assetId={office.assetId!} />);
  // A .docx in "เขียนครับ", the light WebAssembly Word editor (ADR 0057).
  const word = matchRoute('/word/:assetId', path);
  if (word) return requireAuth(<WordEditorPage key={word.assetId} assetId={word.assetId!} />);
  // "ทั้งหมดในโฟลเดอร์นี้" (ADR 0043): /folders/<id>, /folders/root (ยังไม่จัดโฟลเดอร์), /folders (everything).
  if (path === '/folders') return requireAuth(<FolderPage folderId="all" />);
  const folderRoute = matchRoute('/folders/:folderId', path);
  if (folderRoute) return requireAuth(<FolderPage folderId={folderRoute.folderId!} />);
  if (path === '/photo') return requireAuth(<PhotoHomePage />);
  if (path === '/photo/collage') return requireAuth(<PhotoCollagePage />);
  const photo = matchRoute('/photo/:assetId', path);
  if (photo) return requireAuth(<PhotoStudioPage key={photo.assetId} assetId={photo.assetId!} />);
  if (path === '/reviews') return requireAuth(<ReviewsPage />);
  const approvalHistory = matchRoute('/approvals/history/:type/:id', path);
  if (approvalHistory && ['design', 'tor', 'product', 'file'].includes(approvalHistory.type!)) {
    return requireAuth(<ApprovalHistoryPage key={approvalHistory.id} type={approvalHistory.type as ApprovalTargetType} id={approvalHistory.id!} />);
  }
  const approval = matchRoute('/approvals/:requestId', path);
  if (approval) return requireAuth(<ApprovalReviewPage key={approval.requestId} requestId={approval.requestId!} />);
  const sign = matchRoute('/sign/:requestId', path);
  if (sign) return requireAuth(<SignPage key={sign.requestId} requestId={sign.requestId!} />);
  if (path === '/tasks') return requireAuth(<TasksPage />);
  if (path === '/customers') return requireAuth(<CustomersPage />);
  if (path === '/chat') return requireAuth(<ChatPage conversationId={null} focusMessage={null} popout={false} />);
  const chat = matchRoute('/chat/:conversationId', path);
  if (chat) return requireAuth(<ChatPage conversationId={chat.conversationId!} focusMessage={loc.query.get('m')} popout={loc.query.get('popout') === '1'} />);
  if (path === '/channels') return requireAuth(<ChannelsPage />);
  if (path === '/activity') return requireAuth(<ActivityPage />);
  const customer = matchRoute('/customers/:customerId', path);
  if (customer) return requireAuth(<CustomerPage key={customer.customerId} customerId={customer.customerId!} />);
  if (path === '/letters') return requireAuth(<LettersPage />);
  const letter = matchRoute('/letters/:letterId', path);
  if (letter) return requireAuth(<LetterPage key={letter.letterId} letterId={letter.letterId!} />);
  if (path === '/solutions') return requireAuth(<SolutionsListPage />);
  if (path === '/solutions/new') return requireAuth(<SolutionNewPage />);
  const opportunity = matchRoute('/solutions/:opportunityId', path);
  if (opportunity) return requireAuth(<SolutionPage key={opportunity.opportunityId} opportunityId={opportunity.opportunityId!} />);

  if (path === '/catalog') return requireAuth(<CatalogListPage />);
  if (path === '/catalog/import') return requireAuth(<CatalogImportPage />);
  if (path === '/create-document') return requireAuth(<CreateDocumentPage />);
  if (path === '/catalog/deck') return requireAuth(<ProductDeckPage />);
  const item = matchRoute('/catalog/items/:itemId', path);
  if (item) return requireAuth(<ProductPage key={item.itemId} itemId={item.itemId!} />);

  if (path === '/tor') return requireAuth(<TorListPage />);
  if (path === '/tor/new') return requireAuth(<TorWizardPage />);
  if (path === '/tor/library') return requireAuth(<TorLibraryPage />);
  if (path === '/tor/templates') return requireAuth(<TorTemplatesPage />);
  const tpl2 = matchRoute('/tor/templates/:templateId', path);
  if (tpl2) return requireAuth(<TorTemplatePage key={tpl2.templateId} templateId={tpl2.templateId!} />);
  if (path === '/convert') return requireAuth(<ConvertPage />);
  if (path === '/pdf') return requireAuth(<PdfToolsPage />);
  if (path === '/scan') return requireAuth(<ScanPage />);
  if (path === '/help' || path.startsWith('/help/')) return requireAuth(<HelpPage path={path} query={loc.query} />);
  const tor = matchRoute('/tor/drafts/:draftId', path);
  if (tor) return requireAuth(<TorEditorPage key={tor.draftId} draftId={tor.draftId!} />);

  return (
    <FullPageMessage title="ไม่พบหน้านี้" message={`ไม่มีหน้า ${path}`}>
      <a className="xc-btn xc-btn-primary" href="#/">
        กลับหน้าหลัก
      </a>
    </FullPageMessage>
  );
}
